import { z } from "zod";
import { zfd } from "zod-form-data";
import { extendZodWithOpenApi } from "zod-openapi";
import { apiLink, errorResponse, formBody, ind, jsonBody, successResponse, tagPaths, unref } from "../util.ts";
import {
    billing_account_id,
    created_at,
    datetime,
    deleted_at,
    designated_pool_uuid,
    host_pool_uuid,
    host_uuid,
    int,
    simpleSuccessResponse,
    user_id,
    uuid,
    vm_uuid,
} from "./common.ts";
import * as params from "./params.ts";
import {
    VirtualMachine,
    VirtualMachineStorage,
    VmCreateBody,
    VmDeleteBody,
    VmImage,
    VmImageOrderResponse,
    VmImageType,
    VmModifyBody,
    VmResizeDiskBody,
    VmStartBody,
    VmStartCustomRescueBody,
    VmStopBody,
} from "./vm.ts";

extendZodWithOpenApi(z);

//#region Network limits conf

export const IftuneConf = z
    .object({
        average_kbps: int
            .min(0)
            .describe("Average bandwidth limit in kibibytes per second. Omit or set to `0` for unlimited bandwidth"),
        peak_kbps: int
            .min(0)
            .describe(
                "Peak bandwidth limit in kibibytes per second during bursts. Omit or set to `0` for unlimited bandwidth",
            ),
        burst_kb: int
            .min(0)
            .describe("Burst size allowance in kibibytes. Omit or set to `0` to remove the burst limit"),
    })
    .openapi({ ref: "IftuneConf" });

export const IftuneConfBody = z
    .object({
        average_kbps: zfd.numeric(IftuneConf.shape.average_kbps).optional(),
        peak_kbps: zfd.numeric(IftuneConf.shape.peak_kbps).optional(),
        burst_kb: zfd.numeric(IftuneConf.shape.burst_kb).optional(),
    })
    .openapi({ ref: "IftuneConfBody" });

export const VmIftuneBody = IftuneConfBody.extend({
    uuid: vm_uuid,
}).openapi({ ref: "VmIftuneBody" });

//#endregion

const AdminVirtualMachine = VirtualMachine.extend({
    designated_pool_id: z.number().describe("Internal numeric identifier of the designated host pool"),
    hypervisor_uuid: unref(host_uuid).optional().describe("Current hypervisor hosting the VM"),
    is_current_pool_visible: z.boolean().describe("Whether the current pool should be shown in admin views"),
    is_designated_pool_visible: z.boolean().describe("Whether the designated pool should be shown in admin views"),
    latest_host_pool_uuid: unref(host_pool_uuid).describe("Most recent host pool UUID known for this VM"),
    deleted_at: unref(deleted_at).nullable().optional().describe("Deletion timestamp when the VM has been deleted"),
    service_uuid: uuid.describe("Owning service package UUID when the VM is managed as part of a service").optional(),
}).openapi({ ref: "AdminVirtualMachine" });

export const VmMigrateBody = z
    .object({
        uuid: vm_uuid,
        pool_uuid: unref(host_pool_uuid)
            .optional()
            .describe(
                "Target host pool. Used only when `hv_uuid` is omitted; the backend chooses a host from the pool.",
            ),
        hv_uuid: unref(host_uuid)
            .optional()
            .describe("Specific hypervisor to migrate to. When set, this overrides `pool_uuid`."),
    })
    .openapi({ ref: "VmMigrateBody" });

export const VmDesignatedBody = z.object({ uuid: vm_uuid, designated_pool_uuid }).openapi({ ref: "VmDesignatedBody" });

export const AdminVmCreateBody = z
    .object({
        user_id: unref(user_id).describe("User who will own the new VM"),
        billing_account_id,
        name: VmCreateBody.shape.name,
        description: VmCreateBody.shape.description,
        os_name: VmCreateBody.shape.os_name,
        os_version: VmCreateBody.shape.os_version,
        boot_firmware: VmCreateBody.shape.boot_firmware,
        username: VmCreateBody.shape.username,
        password: VmCreateBody.shape.password,
        public_keys: VmCreateBody.shape.public_keys,
        cloud_init: VmCreateBody.shape.cloud_init,
        ram: VmCreateBody.shape.ram,
        vcpu: VmCreateBody.shape.vcpu,
        designated_pool_uuid: VmCreateBody.shape.designated_pool_uuid,
        network_uuid: VmCreateBody.shape.network_uuid,
        disk_uuid: VmCreateBody.shape.disk_uuid.sourceType().unwrap(),
        backup: VirtualMachine.shape.backup.optional(),
    })
    .openapi({ ref: "AdminVmCreateBody" });

export const AdminVmLimits = z
    .object({
        vcpu: z.number().describe("Maximum total vCPU allocation allowed for a LIMITED billing account."),
        ram_mb: z.number().describe("Maximum total RAM allocation in megabytes allowed for a LIMITED billing account."),
    })
    .openapi({ ref: "AdminVmLimits" });

const AdminVmLimitsUpdate = z
    .object({
        vcpu: AdminVmLimits.shape.vcpu.optional(),
        ram_mb: AdminVmLimits.shape.ram_mb.optional(),
    })
    .openapi({ ref: "AdminVmLimitsUpdate" });

export const VmActionLog = z
    .object({
        action: z
            .string()
            .describe("Recorded lifecycle action, such as `create`, `start`, `stop`, or migration events"),
        created_at,
        guest_uuid: unref(vm_uuid).describe("Virtual machine affected by the action"),
        acting_user_id: unref(user_id).optional().describe("User who triggered the action when available"),
        host_uuid: unref(host_uuid).optional().describe("Host on which the action took place, when recorded"),
        hypervisor_uuid: unref(host_uuid)
            .optional()
            .describe(
                "Related hypervisor UUID, such as the migration destination or the active hypervisor for the action",
            ),
    })
    .openapi({ ref: "VmActionLog" });

export const VmActionLogResponse = z.array(VmActionLog).openapi({ ref: "VmActionLogResponse" });

const param_guest_uuid = unref(vm_uuid)
    .optional()
    .describe("Only return actions for this virtual machine")
    .openapi({ param: { name: "guest_uuid", in: "query" } });

const param_hypervisor_uuid = unref(host_uuid)
    .optional()
    .describe("Only return actions involving this hypervisor")
    .openapi({ param: { name: "hypervisor_uuid", in: "query" } });

const param_since = unref(datetime)
    .optional()
    .describe("Only return actions created at or after this UTC timestamp")
    .openapi({ param: { name: "since", in: "query" } });

const nestedStatisticsSchema = z.object({
    running: z.number().describe("Number of running VMs in this bucket"),
    stopped: z.number().describe("Number of stopped VMs in this bucket"),
});

export const VmAdminStatistics = z
    .object({
        by_cpu: z.record(z.string(), nestedStatisticsSchema).describe("Counts grouped by vCPU amount"),
        by_mem: z
            .record(z.string(), nestedStatisticsSchema)
            .describe("Counts grouped by RAM size in gigabytes. Fractional keys can appear for legacy sizes."),
        by_os: z.record(z.string(), nestedStatisticsSchema).describe("Counts grouped by operating system name"),
    })
    .openapi({ ref: "VmAdminStatistics" });

export const VmResetPasswordBody = z.object({ uuid: vm_uuid }).openapi({ ref: "VmResetPasswordBody" });

export const VmResetPasswordResponse = z
    .object({ username: z.string(), password: z.string() })
    .openapi({ ref: "VmResetPasswordResponse" });

const image_type_path = VmImageType.openapi({ param: { name: "image_type", in: "path" } });
const os_name_path = z
    .string()
    .describe("Stable VM image name prefix stored under the selected catalog")
    .openapi({ param: { name: "os_name", in: "path" } });

const uuid_query_for_vm = vm_uuid.openapi({ param: { name: "uuid", in: "query" } });

export const vmAdminPaths = tagPaths("admin_vm")({
    "/config/network/iftune": {
        get: {
            summary: "Get global iftune configuration",
            description: ind`
                Shows the current global \`iftune\` parameters for VM network interfaces.

                An empty object means nothing is configured, which is equivalent to all limits being \`0\` and bandwidth being unlimited.

                See [libvirt network QoS documentation](https://libvirt.org/formatnetwork.html#quality-of-service) for more information.
            `,
            responses: {
                ...successResponse(IftuneConf),
            },
        },
        put: {
            summary: "Set global iftune configuration",
            description: ind`
                Modifies the global \`iftune\` parameters for VM network interfaces.

                The change affects VMs without VM-specific overrides that are started after this configuration is updated.

                Send the request without values, or set values to \`0\`, to remove bandwidth limits.

                It is recommended to test the same values on a specific running VM first to confirm libvirt accepts them before applying them globally.

                See [libvirt network QoS documentation](https://libvirt.org/formatnetwork.html#quality-of-service) for more information.
            `,
            requestBody: formBody(IftuneConfBody),
            responses: {
                ...successResponse(IftuneConf),
            },
        },
    },

    // Admin VM
    "/{location}/user-resource/admin/vm": {
        get: {
            summary: "Get virtual machine",
            parameters: [params.location, uuid_query_for_vm],
            responses: {
                ...successResponse(AdminVirtualMachine),
            },
        },
        post: {
            summary: "Create virtual machine for a user",
            description: ind`
                Creates a virtual machine on behalf of another user. For the general creation flow, see [Create VM](${apiLink(
                    "/{location}/user-resource/vm",
                    "vm",
                    "post",
                )}).

                Admin-specific restrictions from Postman that still map to the current API:

                - \`disk_uuid\` is mandatory and the VM is created from that existing disk, so \`os_name\` and \`os_version\` are mainly descriptive metadata.
                - This route does not reserve or assign a public IP during creation.
                - The VM is created in the stopped state. Start it separately after creation.
                - \`username\`, \`password\`, \`public_keys\`, and \`cloud_init\` are only passed as cloud-init user data; if cloud-init has already initialized the attached disk, they might have no effect.
            `,
            parameters: [params.location],
            requestBody: formBody(AdminVmCreateBody),
            responses: {
                ...successResponse(AdminVirtualMachine),
                ...errorResponse({
                    errors: {
                        disk_uuid: "mandatory parameter 'disk_uuid' missing",
                        user_id: "mandatory parameter 'user_id' missing",
                    },
                }),
            },
        },
        delete: {
            summary: "Delete a virtual machine",
            parameters: [params.location],
            requestBody: jsonBody(VmDeleteBody),
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },
    "/{location}/user-resource/admin/vm/start": {
        post: {
            summary: "Start virtual machine",
            description: "Starts the specified VM regardless of ownership.",
            parameters: [params.location],
            requestBody: formBody(VmStartBody),
            responses: {
                ...successResponse(AdminVirtualMachine),
            },
        },
    },
    "/{location}/user-resource/admin/vm/stop": {
        post: {
            summary: "Stop virtual machine",
            description:
                "Admin equivalent of the end-user stop operation. The optional `force` flag behaves the same way as on the user-facing stop route.",
            parameters: [params.location],
            requestBody: formBody(VmStopBody),
            responses: {
                ...successResponse(AdminVirtualMachine),
            },
        },
    },

    "/{location}/base-operator/migrate": {
        post: {
            summary: "Migrate virtual machine",
            description: ind`
                Migrates a VM to another hypervisor or host pool.

                If \`hv_uuid\` is specified, that hypervisor is used and \`pool_uuid\` is ignored.
                If only \`pool_uuid\` is specified, the backend chooses a migration target from that pool.
                If neither target is provided, the backend chooses a destination from the VM's current host pool.
            `,
            parameters: [params.location],
            requestBody: formBody(VmMigrateBody),
            responses: {
                ...successResponse(AdminVirtualMachine),
            },
        },
    },

    "/{location}/base-operator/admin/vm/iftune": {
        put: {
            summary: "Override VM iftune",
            description: ind`
                Sets VM-specific network bandwidth limits and applies them immediately when the VM is running.

                The returned VM includes the stored override in \`nic_iftune\`.

                Send only \`uuid\` to clear the VM-specific override and fall back to global limits.

                Set \`average_kbps=0\` to force unlimited bandwidth for this VM even when global limits exist. Any omitted burst fields default to \`0\` in that unlimited override case.

                See [libvirt network QoS documentation](https://libvirt.org/formatnetwork.html#quality-of-service) for more information.
            `,
            parameters: [params.location],
            requestBody: jsonBody(VmIftuneBody),
            responses: {
                ...successResponse(AdminVirtualMachine),
            },
        },
    },

    "/config/compute/limits/vm": {
        get: {
            summary: "Get LIMITED billing account VM limits",
            responses: {
                ...successResponse(AdminVmLimits),
            },
        },
        put: {
            summary: "Update LIMITED billing account VM limits",
            description:
                "Updates the VM resource limits applied to billing accounts in the LIMITED restriction level. Lowering the limits does not stop existing resources that are already over the new limit. Route-level note kept from Postman: omitting a field or sending it empty removes that specific limit.",
            requestBody: jsonBody(AdminVmLimitsUpdate),
            responses: {
                ...successResponse(AdminVmLimits),
            },
        },
    },

    "/{location}/base-operator/admin/vm/virsh_dumpxml": {
        get: {
            summary: "Get Domain XML",
            description:
                "Returns the libvirt domain XML for a VM defined on a hypervisor. If the VM is stopped and undefined, the backend returns `400 Bad Request`.",
            parameters: [params.location, uuid_query_for_vm],
            responses: {
                ...successResponse(
                    z
                        .string()
                        .describe("libvirt domain XML definition of the VM")
                        .openapi({ ref: "VmDomainXml", contentMediaType: "application/xml" }),
                ),
            },
        },
    },

    "/{location}/user-resource/admin/vm/designated_pool": {
        patch: {
            summary: "Change designated pool for virtual machine",
            description: "Changes the VM's designated host pool, which is used as the preferred placement target.",
            parameters: [params.location],
            requestBody: formBody(VmDesignatedBody),
            responses: {
                ...successResponse(AdminVirtualMachine),
            },
        },
    },

    "/{location}/base-operator/admin/action_log": {
        get: {
            summary: "Get action log for virtual machine",
            description:
                "Returns VM lifecycle actions such as create, start, stop, and migrations. All filters are optional and can be combined.",
            parameters: [params.location, param_since, param_hypervisor_uuid, param_guest_uuid],
            responses: {
                ...successResponse(VmActionLogResponse),
            },
        },
    },

    "/{location}/base-operator/admin/vm": {
        patch: {
            summary: "Resize a virtual machine",
            description: "Changes the VM's RAM or vCPU allocation. The VM must be stopped before resizing.",
            parameters: [params.location],
            requestBody: formBody(VmModifyBody),
            responses: {
                ...successResponse(AdminVirtualMachine),
            },
        },
    },

    "/{location}/base-operator/admin/vm/storage": {
        patch: {
            summary: "Resize a virtual machine disk",
            description: "Changes the size of a VM disk. The new size cannot be smaller than the current disk size.",
            parameters: [params.location],
            requestBody: formBody(VmResizeDiskBody),
            responses: {
                ...successResponse(VirtualMachineStorage),
            },
        },
    },

    "/{location}/user-resource/admin/vm/boot_iso_media": {
        post: {
            summary: "Start virtual machine in rescue mode",
            description: ind`
                Admin variant of [Boot a rescue or installation media](${apiLink(
                    "/{location}/user-resource/vm/boot_iso_media",
                    "vm",
                    "post",
                )}).

                The VM starts once from the specified boot media and then returns to its normal boot order on the next start.
            `,
            parameters: [params.location],
            requestBody: formBody(VmStartCustomRescueBody),
            responses: {
                ...successResponse(AdminVirtualMachine),
            },
        },
    },

    "/{location}/base-operator/admin/vm/statistics/chart": {
        get: {
            summary: "Get virtual machine statistics",
            description:
                "Returns platform-wide counts of running and stopped VMs grouped by vCPU amount, RAM size, and operating system.",
            parameters: [params.location],
            responses: {
                ...successResponse(VmAdminStatistics),
            },
        },
    },

    // Vm images
    "/config/vm_images/{image_type}": {
        post: {
            summary: "Create VM image",
            description: "Add a new VM image entry to the selected image catalog.",
            parameters: [image_type_path],
            requestBody: jsonBody(VmImage),
            responses: {
                ...successResponse(VmImage),
            },
        },
    },
    "/config/vm_images/{image_type}/{os_name}": {
        get: {
            summary: "Get VM image",
            description: "Get a specific VM image configuration by catalog type and OS name.",
            parameters: [image_type_path, os_name_path],
            responses: {
                ...successResponse(VmImage),
            },
        },
        patch: {
            summary: "Update VM image",
            description: "Update the stored configuration for a VM image entry in the selected catalog.",
            parameters: [image_type_path, os_name_path],
            requestBody: jsonBody(VmImage),
            responses: {
                ...successResponse(VmImage),
            },
        },
        delete: {
            summary: "Delete VM image",
            description: "Delete a VM image configuration from the selected catalog.",
            parameters: [image_type_path, os_name_path],
            responses: {
                ...simpleSuccessResponse,
            },
        },
    },
    "/config/vm_images_order/{image_type}": {
        put: {
            summary: "Order VM images",
            parameters: [image_type_path],
            requestBody: jsonBody(VmImageOrderResponse),
            responses: {
                ...successResponse(VmImageOrderResponse),
            },
        },
    },

    "/{location}/base-operator/admin/vm/user/generate_password": {
        post: {
            summary: "Reset virtual machine password",
            parameters: [params.location],
            requestBody: formBody(VmResetPasswordBody),
            responses: {
                ...successResponse(VmResetPasswordResponse),
            },
        },
    },
});
